// src/pages/ServiciosPage.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import Header from '../components/Header'; // IMPORTAMOS EL HEADER

// Importa los iconos y datos de servicios
import { 
  ClipboardDocumentCheckIcon, 
  ExclamationTriangleIcon, 
  Squares2X2Icon, 
  MapPinIcon,
  DocumentMagnifyingGlassIcon, 
  UserGroupIcon, 
  DocumentTextIcon, 
  AcademicCapIcon,
  ChartBarIcon, 
  BellAlertIcon, 
  ScaleIcon
} from '@heroicons/react/24/outline';

// Datos de servicios (igual que antes)
const services = [
  { icon: ClipboardDocumentCheckIcon, title: "Sistema de Inspecciones PWA", description: "Aplicativo web progresivo para inspecciones de mantenimiento, seguridad industrial y medio ambiente.", features: ["Checklists personalizables", "Modo offline", "Captura de evidencias", "Reportes automáticos"] },
  { icon: ExclamationTriangleIcon, title: "Gestión de Riesgos Laborales", description: "Identificación de peligros y evaluación de riesgos en puestos de trabajo.", features: ["Matrices de riesgo", "Análisis por área", "Plan de acción", "Seguimiento continuo"] },
  { icon: Squares2X2Icon, title: "Órdenes de Trabajo con Kanban", description: "Gestión de tareas con tablero Kanban y notificaciones automáticas.", features: ["Kanban visual", "Notificaciones Telegram", "Seguimiento 7-3-1 días", "Asignación dinámica"] },
  { icon: MapPinIcon, title: "Registro de Visitas con Geolocalización", description: "Control de visitas con geolocalización, fotos y dictado por voz.", features: ["Geolocalización", "Multimedia integrado", "Voz a texto", "Reportes PDF"] },
  { icon: DocumentMagnifyingGlassIcon, title: "Investigación de Accidentes", description: "Análisis con método de espina de pescado y 5 porqués.", features: ["Diagramas Ishikawa", "Plan de acciones", "Asignación responsables", "Seguimiento medidas"] },
  { icon: UserGroupIcon, title: "Gestión de RRHH", description: "Administración de empleados y capacitaciones con búsqueda por DNI.", features: ["Registro completo", "Gestión capacitaciones", "Búsqueda ágil", "Historial digital"] },
  { icon: DocumentTextIcon, title: "Firma Digital", description: "Sistema de firma digital con trazabilidad completa.", features: ["Geolocalización", "Fecha/hora certificada", "Responsable identificado", "Archivo seguro"] },
  { icon: AcademicCapIcon, title: "Estudios Ergonómicos", description: "Protocolos según normativa argentina con anexos completos.", features: ["Protocolos oficiales", "Cálculos automáticos", "Recomendaciones", "Reportes normativos"] },
  { icon: ChartBarIcon, title: "Mediciones Ambientales", description: "Registro de ruido e iluminación según resolución legal.", features: ["Cumplimiento legal", "Registros históricos", "Alertas límites", "Reportes oficiales"] },
  { icon: BellAlertIcon, title: "Avisos de Riesgos", description: "Gestión de alertas por criticidad y responsables.", features: ["Priorización automática", "Asignación áreas", "Seguimiento", "Cierre validado"] },
  { icon: ScaleIcon, title: "Control de Pesaje", description: "Registro de kilos en cargas/descargas con PDF detallado.", features: ["Cálculo automático", "PDF personalizado", "Histórico completo", "Integración silos"] },
];

const ServiciosPage: React.FC = () => {
  return (
    <>
     <SEO
  title="Servicios de Desarrollo de Software | Algorithmia Córdoba"
  description="Expertos en desarrollo de software a medida en Córdoba. Ofrecemos sistemas de gestión empresarial, aplicaciones PWA, automatización industrial y software de seguridad laboral."
  keywords="desarrollo software córdoba, sistemas gestión empresarial, app mantenimiento industrial, software seguridad laboral, pwa argentina"
  url="https://algorithmia.com.ar/servicios"
  canonicalUrl="https://algorithmia.com.ar/servicios"  // ✅ agregado
/>

      
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        {/* HEADER REUTILIZABLE */}
        <Header isSplashLoading={false} />

        {/* Hero - Ajustado con padding-top para no quedar debajo del header */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-[#0E2544] to-[#2D4A7D] text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              Nuestros <span className="text-[#4ADE80]">Servicios</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-gray-300 max-w-3xl mx-auto"
            >
              Soluciones de software personalizadas para impulsar la transformación digital de tu empresa en Argentina.
            </motion.p>
          </div>
        </section>

        {/* Grid de Servicios */}
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 group"
                  >
                    <div className="flex items-center gap-4 mb-6">
                      <div className="p-3 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] rounded-xl text-white shadow-lg group-hover:scale-110 transition-transform">
                        <Icon className="h-8 w-8" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">{service.title}</h3>
                    </div>
                    <p className="text-gray-600 mb-6">{service.description}</p>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                          <CheckCircleIcon className="h-5 w-5 text-[#4ADE80] flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Footer simple */}
        <footer className="py-8 bg-white border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-gray-600 text-sm">
              © {new Date().getFullYear()} Algorithmia. Todos los derechos reservados.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ServiciosPage;